import { z } from 'zod'
import { CURRENT_AUTH_LETTER_VERSION } from '../redux/profile/ProfileSlice'
import { phoneNumberSchema } from './util'
import { timeAgo } from '../utils/dateHelpers'
import { states, normalizeMiddleName } from '../utils/helpers'
import { isArray } from 'lodash'

const addressSchema = z.object({
  city: z
    .string()
    .nonempty('City is required')
    .max(100, 'City cannot be longer than 100 characters')
    .refine((val) => val.trim().length > 0, {
      message: 'City cannot be empty or just spaces',
    }),
  state: z.enum(states, {
    errorMap: () => ({
      message: 'Must be a valid 2 letter state code',
    }),
  }),
  streetAddress: z
    .string()
    .nonempty('Street Address is required')
    .max(100, 'Street Address cannot be longer than 100 characters')
    .refine((val) => val.trim().length > 0, {
      message: 'Street Address cannot be empty or just spaces',
    }),
  zip: z
    .string()
    .nonempty('Zip is required')
    .max(10, 'Zip cannot be longer than 10 characters')
    .refine((val) => val.trim().length > 0, {
      message: 'Zip cannot be empty or just spaces',
    }),
})

export const profileSchema = z.object({
  firstName: z
    .object({
      value: z
        .string()
        .nonempty('First name is required')
        .max(50, 'Name cannot be more than 50 characters')
        .regex(
          /^[a-zA-Z]+(?:[\s-][a-zA-Z]+)*$/,
          'First name can only contain letters and hyphens (-)',
        ),
    })
    .array(),
  middleName: z
    .object({
      value: z
        .string()
        .nonempty('Middle name is required')
        .max(50, 'Name cannot be more than 50 characters')
        .regex(
          /^(?:[a-zA-Z]+(?:[\s-][a-zA-Z]+)*)?$/,
          'Middle name can only contain letters and hyphens (-)',
        ),
    })
    .array(),
  lastName: z
    .object({
      value: z
        .string()
        .max(50, 'Name cannot be more than 50 characters')
        .nonempty('Last name is required')
        .regex(
          /^[a-zA-Z]+(?:[\s-][a-zA-Z]+)*$/,
          'Last name can only contain letters and hyphens (-)',
        ),
    })
    .array(),
  otherFirstName: z
    .object({
      value: z
        .string()
        .max(50, 'Name cannot be more than 50 characters')
        .regex(
          /^$|^[a-zA-Z]+(?:[\s-][a-zA-Z]+)*$/,
          'First name can only contain letters and hyphens (-)',
        )
        .optional(),
    })
    .array()
    .optional(),
  otherMaidenName: z
    .object({
      value: z
        .string()
        .max(50, 'Name cannot be more than 50 characters')
        .regex(
          /^$|^[a-zA-Z]+(?:[\s-][a-zA-Z]+)*$/,
          'Maiden name can only contain letters and hyphens (-)',
        )
        .optional(),
    })
    .array()
    .optional(),
  phoneNumber: z
    .object({
      value: phoneNumberSchema,
    })
    .array(),
  email: z
    .object({
      value: z.string().email().nonempty('Email is required').max(100),
    })
    .array(),
  birthdate: z.coerce
    .date()
    .max(
      timeAgo({ years: 18 }),
      'You must be at least 18 years old to use mePrism',
    )
    .min(
      timeAgo({ years: 100 }),
      'You must be under 100 years old to use mePrism',
    )
    .nullable()
    .refine((val) => val !== null, {
      message: 'Please select a valid birthdate',
    }),
  address: addressSchema.array(),
  mp_auth_letter_signature: z.string().nonempty().max(50),
  auth_letter_s3_key: z.string(),
  gender: z.string(),
  auth_letter_version: z.string(),
  linkedIn: z
    .string()
    .trim()
    .url('Invalid LinkedIn URL')
    .max(120, 'LinkedIn URL cannot exceed 120 characters')
    .regex(
      /^https:\/\/([a-z]{2,3}\.)?linkedin\.com\/.*$/,
      'LinkedIn URL must be a valid LinkedIn profile link',
    )
    .optional()
    .or(z.literal('')),
  agreeToTerms: z
    .boolean()
    .refine((value) => value === true, {
      message: 'You must agree to the terms and conditions',
    })
    .optional(),
  metadata: z.object({
    dob_counter: z.number().nullable().optional(),
  }).optional(),
})

// eslint-disable-next-line @typescript-eslint/no-unused-vars
export const createAddressSchema = (isStreetOptional: boolean) => {
  const addressSchema = z.object({
    city: z
      .string()
      .nonempty('City is required')
      .max(100, 'City cannot be longer than 100 characters')
      .refine((val) => val.trim().length > 0, {
        message: 'City cannot be empty or just spaces',
      }),
    state: z.enum(states, {
      errorMap: () => ({
        message: 'Must be a valid 2 letter state code',
      }),
    }),
    streetAddress: z
      .string()
      .nonempty('Street Address is required')
      .max(100, 'Street Address cannot be longer than 100 characters')
      .refine((val) => val.trim().length > 0, {
        message: 'Street Address cannot be empty or just spaces',
      }),
    zip: z
      .string()
      .nonempty('Zip is required')
      .max(10, 'Zip cannot be longer than 10 characters')
      .regex(/^\d*$/, 'Zip code must contain only numbers')
      .refine((val) => val.trim().length > 0, {
        message: 'Zip cannot be empty or just spaces',
      }),
  })
  return addressSchema.array()
}

export const createProfileSchema = (
  isLinkedinOptional: boolean,
  isStreetOptional: boolean,
  agreeToTermsOptional: boolean,
) => {
  const profileSchema = z.object({
    firstName: z
      .object({
        value: z
          .string()
          // .nonempty('First name is required')
          .max(50, 'Name cannot be more than 50 characters')
          .regex(
            /^[a-zA-Z]+(?:[\s-][a-zA-Z]+)*$/,
            'First name can only contain letters and hyphens (-)',
          ),
      })
      .array(),
    // .optional(),

    // Use the conditional `middleNameSchema` with or without `.optional()`
    middleName: z
      .object({
        value: z
          .string()
          .max(50, 'Name cannot be more than 50 characters')
          .regex(
            /^(?:[a-zA-Z]+(?:[\s-][a-zA-Z]+)*)?$/,
            'Middle name can only contain letters and hyphens (-)',
          )
          .refine((value) => value.length > 0, {
            message: 'Middle name is required',
          }),
      })
      .array(),
    lastName: z
      .object({
        value: z
          .string()
          // .nonempty('Last name is required')
          .max(50, 'Name cannot be more than 50 characters')
          .regex(
            /^[a-zA-Z]+(?:[\s-][a-zA-Z]+)*$/,
            'Last name can only contain letters and hyphens (-)',
          ),
      })
      .array(),
    // .optional(),

    otherFirstName: z
      .object({
        value: z
          .string()
          .max(50, 'Name cannot be more than 50 characters')
          .regex(
            /^$|^[a-zA-Z]+(?:[\s-][a-zA-Z]+)*$/,
            'First Name can only contain letters and hyphens (-)',
          )
          .optional(),
      })
      .array()
      .optional(),

    otherMaidenName: z
      .object({
        value: z
          .string()
          // .nonempty('Last name is required')
          .max(50, 'Name cannot be more than 50 characters')
          .regex(
            /^$|^[a-zA-Z]+(?:[\s-][a-zA-Z]+)*$/,
            'Maiden name can only contain letters and hyphens (-)',
          )
          .optional(),
      })
      .array()
      .optional(),

    phoneNumber: z.object({ value: phoneNumberSchema }).array(),
    email: z.object({ value: z.string().email().max(100) }).array(),
    birthdate: z.coerce
      .date()
      .max(
        timeAgo({ years: 18 }),
        'You must be at least 18 years old to use mePrism',
      )
      .min(
        timeAgo({ years: 100 }),
        'You must be under 100 years old to use mePrism',
      )
      .nullable()
      .refine((val) => val !== null, {
        message: 'Please select a valid birthdate',
      }),
    address: createAddressSchema(isStreetOptional),
    mp_auth_letter_signature: z.string().max(50),
    auth_letter_s3_key: z.string(),
    gender: z.string(),
    auth_letter_version: z.string(),
    linkedIn: isLinkedinOptional
      ? z
          .string()
          .max(120, 'LinkedIn URL cannot exceed 120 characters')
          .refine(
            (value) =>
              !value ||
              (/^https:\/\/([a-z]{2,3}\.)?linkedin\.com\/.*$/.test(value) && !/\s/.test(value)),
            {
              message:
                'Invalid LinkedIn URL. Please use the format: https://www.linkedin.com/your-profile',
            },
          )
          .optional()
      : z
          .string()
          .max(120, 'LinkedIn URL cannot exceed 120 characters')
          .refine((value) => value.length > 0, {
            message: 'LinkedIn URL is required',
          })
          .refine(
            (value) =>
              !value ||
              (/^https:\/\/([a-z]{2,3}\.)?linkedin\.com\/.*$/.test(value) && !/\s/.test(value)),
            {
              message:
                'Invalid LinkedIn URL. Please use the format: https://www.linkedin.com/your-profile',
            },
          ),
    agreeToTerms: agreeToTermsOptional
      ? z.boolean().refine((value) => value === true, {
          message: 'You must agree to the terms and conditions',
        })
      : z.boolean().optional(),
    metadata: z.object({
      dob_counter: z.number().nullable().optional(),
    }).optional(),
  })

  return profileSchema
}

export const emptyProfileSchema = (): z.TypeOf<typeof profileSchema> => ({
  firstName: [{ value: '' }],
  middleName: [{ value: '' }],
  lastName: [{ value: '' }],
  otherFirstName: [{ value: '' }],
  otherMaidenName: [{ value: '' }],
  address: [
    {
      city: '',
      state: '' as 'CA',
      streetAddress: '',
      zip: '',
    },
  ],
  email: [{ value: '' }],
  phoneNumber: [{ value: '' }],
  birthdate: new Date(),
  mp_auth_letter_signature: '',
  auth_letter_s3_key: '',
  gender: '',
  auth_letter_version: CURRENT_AUTH_LETTER_VERSION,
  linkedIn: '',
  agreeToTerms: false,
})

export const asProfilePost = (schema: z.TypeOf<typeof profileSchema>) => ({
  names: {
    first: schema.firstName.map(({ value }) => value).filter(Boolean),
    middle: schema.middleName.map(({ value }) => normalizeMiddleName(value)).filter(Boolean),
    last: schema.lastName.map(({ value }) => value).filter(Boolean),
  },
  emails: schema.email
    .map(({ value }) => value)
    .filter(Boolean)
    .map((address) => ({ address, validated: false })),
  phones: schema.phoneNumber
    ? schema.phoneNumber
        .map(({ value }) => value)
        .filter(Boolean)
        .map((number) => ({ number, validated: false }))
    : [],
  birthdate: schema.birthdate ? schema.birthdate.toISOString() : '',
  address: schema.address.map(({ streetAddress, ...rest }) => ({
    street1: streetAddress,
    country: 'US',
    ...rest,
  })),
  userID: '',
  mp_auth_letter_signature: schema.mp_auth_letter_signature,
  linkedIn: schema.linkedIn,
  auth_letter_version: CURRENT_AUTH_LETTER_VERSION,
  name: {
    first: schema.firstName.map(({ value }) => value).filter(Boolean)[0],
    middle: normalizeMiddleName(schema.middleName.map(({ value }) => value).filter(Boolean)[0]),
    last: schema.lastName.map(({ value }) => value).filter(Boolean)[0],
  },
  additional_information: {
    additional_first_name: schema.otherFirstName
      ? schema.otherFirstName
          .map(({ value }) => value)
          .filter((val): val is string => val !== undefined && val !== '')
      : [],
    maiden_name: schema.otherMaidenName
      ? schema.otherMaidenName
          .map(({ value }) => value)
          .filter((val): val is string => val !== undefined && val !== '')
      : [],
  },
  metadata: schema.metadata || { dob_counter: 0 },
})

export const asProfileUpdate = (schema: z.TypeOf<typeof profileSchema>) => ({
  name: {
    first: schema.firstName.map(({ value }) => value).filter(Boolean)[0],
    middle: normalizeMiddleName(schema.middleName.map(({ value }) => value).filter(Boolean)[0]),
    last: schema.lastName.map(({ value }) => value).filter(Boolean)[0],
  },
  names: {
    first: schema.firstName.map(({ value }) => value).filter(Boolean),
    middle: schema.middleName.map(({ value }) => normalizeMiddleName(value)),
    last: schema.lastName.map(({ value }) => value).filter(Boolean),
  },
  emails: schema.email
    .map(({ value }) => value)
    .filter(Boolean)
    .map((address) => ({ address, validated: false })),
  birthdate: schema.birthdate ? schema.birthdate.toISOString() : '',
  phones: schema.phoneNumber
    .map(({ value }) => value)
    .filter(Boolean)
    .map((number) => ({ validated: false, number })),
  address: schema.address.map(({ streetAddress, ...rest }) => ({
    street1: streetAddress,
    country: 'US',
    ...rest,
  })),
  gender: schema.gender,
  mp_auth_letter_signature: schema.mp_auth_letter_signature,
  auth_letter_s3_key: schema.auth_letter_s3_key,
  auth_letter_version: schema.auth_letter_version,
  linkedIn: schema.linkedIn,
  additional_information: {
    additional_first_name: schema.otherFirstName
      ? schema.otherFirstName
          .map(({ value }) => value)
          .filter((val): val is string => val !== undefined && val !== '')
      : [],
    maiden_name: schema.otherMaidenName
      ? schema.otherMaidenName
          .map(({ value }) => value)
          .filter((val): val is string => val !== undefined && val !== '')
      : [],
  },
  metadata: {
    dob_counter: schema.metadata?.dob_counter ?? null,
  },
})

export const ProfileFields = z.object({
  name: z
    .object({
      first: z.string().nullable(),
      middle: z.string().nullable(),
      last: z.string().nullable(),
      suffix: z.string().nullable(),
      title: z.string().nullable(),
    })
    .nullish(),

  names: z.object({
    first: z.array(z.string()),
    middle: z.array(z.string()),
    last: z.array(z.string()),
    suffix: z.array(z.string()),
    title: z.array(z.string()),
  }),

  address: z.array(
    z.object({
      street1: z.string().nullish(),
      street2: z.string().nullish(),
      city: z.string(),
      state: z.string(),
      zip: z.string(),
      country: z.string(),
      address_unified: z.string().nullish(),
    }),
  ),

  emails: z.array(
    z.object({
      address: z.string(),
      validated: z.boolean().nullish(),
    }),
  ),

  phones: z.array(
    z.object({
      number: z.string(),
      validated: z.boolean().optional(),
    }),
  ),

  gender: z.string().nullish(),
  birthdate: z.string(),

  mp_auth_letter_signature: z.string().nullish(),
  auth_letter_version: z.string().nullish(),
  auth_letter_s3_key: z.string().nullish(),

  userID: z.string(),

  linkedIn: z.string().nullish(),

  additional_information: z
    .object({
      additional_first_name: z.array(z.string()).nullish(),
      maiden_name: z.array(z.string()).nullish(),
    })
    .nullish(),

  last_scan_initiated_at: z.string().nullish(),
  metadata: z.object({
    dob_counter: z.number().nullable().optional(),
  }).optional(),
})

export const fromProfileFields = (
  fields: z.TypeOf<typeof ProfileFields>,
): z.TypeOf<typeof profileSchema> => ({
  firstName:
    fields.names && fields.names.first && fields.names.first.length > 0
      ? fields.names.first.map((value) => ({ value }))
      : [{ value: '' }],
  lastName:
    fields.names && fields.names.last && fields.names.last.length > 0
      ? fields.names.last.map((value) => ({ value }))
      : [{ value: '' }],
  middleName:
    fields.names && fields.names.middle && fields.names.middle.length > 0
      ? fields.names.middle.map((value: any) => ({ value }))
      : [{ value: '' }],
  otherFirstName:
    fields.additional_information &&
    fields.additional_information.additional_first_name &&
    fields.additional_information.additional_first_name.length > 0
      ? fields.additional_information.additional_first_name.map(
          (value: string) => ({ value }),
        )
      : [{ value: '' }],
  otherMaidenName:
    fields.additional_information &&
    fields.additional_information.maiden_name &&
    fields.additional_information.maiden_name.length > 0
      ? fields.additional_information.maiden_name.map((value: string) => ({
          value,
        }))
      : [{ value: '' }],
  email:
    fields.emails && isArray(fields.emails)
      ? fields.emails.map(({ address }) => ({ value: address }))
      : [{ value: '' }],
  phoneNumber:
    isArray(fields.phones) && fields.phones.length > 0
      ? fields.phones.map(({ number }) => {
          let val = number || '';
          if (val && !val.startsWith('+')) {
            val = val.length === 10 ? `+1${val}` : `+${val}`;
          }
          return { value: val };
        })
      : [{ value: '' }],
  birthdate: fields.birthdate ? new Date(fields.birthdate) : null,
  // below is mad because it thinks we can't convert a string to state
  // @TODO fix the type ignore
  // @ts-ignore
  address:
    isArray(fields.address) && fields.address.length > 0
      ? fields.address.map(({ city, state, street1: streetAddress, zip }) => ({
          city,
          state,
          streetAddress: streetAddress ?? '',
          zip: zip ?? '',
        }))
      : [{ city: '', state: '', streetAddress: '', zip: '' }],
  mp_auth_letter_signature: fields.mp_auth_letter_signature || '',
  auth_letter_s3_key: fields.auth_letter_s3_key || '',
  gender: fields.gender || '',
  auth_letter_version:
    fields.auth_letter_version || CURRENT_AUTH_LETTER_VERSION,
  linkedIn: fields.linkedIn ? fields.linkedIn : '',
  metadata: fields.metadata,
})
